import React from 'react';
import Helmet from 'react-helmet';
import { Utils, generateUrls } from '@americanexpress/axp-benefits-utils';
import { cleanCardNameCard } from '../../../utilities/card-name-meta-clean';

export const loginUrl = {
  development: 'https://e1qonline.webqa.ipc.us.aexp.com/myca/logon/us/action/LogonHandler?request_type=LogonHandler&Face=en_US&DestPage=https%3A%2F%2Fe1qglobal11.com%2Fcards-benefits%2Fview-all/?inav=MYCA_PC_Benefits2#/',
  integration: 'https://e1qonline.webqa.ipc.us.aexp.com/myca/logon/us/action/LogonHandler?request_type=LogonHandler&Face=en_US&DestPage=https%3A%2F%2Fe1qglobal11.com%2Fcards-benefits%2Fview-all/?inav=MYCA_PC_Benefits2#/',
  qa: 'https://e2qonline.americanexpress.com/myca/logon/us/action/LogonHandler?request_type=LogonHandler&Face=en_US&DestPage=https%3A%2F%2Fe2qonline.americanexpress.com%2Fcards-benefits%2Fview-all/?inav=MYCA_PC_Benefits2#/',
  production: 'https://online.americanexpress.com/myca/logon/us/action/LogonHandler?request_type=LogonHandler&Face=en_US&DestPage=https%3A%2F%2Fonline.americanexpress.com%2Fcards-benefits%2Fview-all/?inav=MYCA_PC_Benefits2#/',
}[process.env.NODE_ENV];

export const renderViewAllTitle = (pagetitle, locale) => {
  const cardName = pagetitle?.split('|')[0].trim();
  const cardNameShort = cleanCardNameCard(cardName);

  return (
    <> {global.BROWSER && (
      <Helmet
        encodeSpecialCharacters={true}
        title={locale === 'ja-JP' ? `${cardName}` : `${cardName} | Card Benefits | American Express`}
        meta={[
          { name: 'description', content: `Learn More About the Many Benefits That ${cardNameShort} Cardholders Enjoy! Get to know the Benefits & Rewards of the ${cardName}.` },
          { name: 'keywords', content: `${cardName}, ${cardName} Benefits` },
          { name: 'robots', content: '' },
        ]}
        link={[
          { rel: 'canonical', href: `${window.location.href}` },
        ]}
      />
    )}
    </>
  );
};

export const isTallByRow = (order, columns, longTiles) => {
  // returns an array of bools corresponding to rows where true means tall
  const tallArray = [];
  // only loop through one row instead of each item in the row
  for (let i = 0; i < order.length; i += columns) {
    const id = order[i];
    switch (columns) {
      case 3: {
        const idMiddle = order[i + 1];
        const idLast = order[i + 2];
        if (longTiles.includes(id)
          || longTiles.includes(idMiddle)
          || longTiles.includes(idLast)) { // check if any of the 3 are long
          tallArray.push(true);
        } else {
          tallArray.push(false);
        }
        break;
      }
      case 2: {
        const idSecond = order[i + 1];
        if (longTiles.includes(id)
          || longTiles.includes(idSecond)) { // check if either of the 2 are long
          tallArray.push(true);
        } else {
          tallArray.push(false);
        }
        break;
      }
      case 1:
        if (longTiles.includes(id)) { // check if the one is long
          tallArray.push(true);
        } else {
          tallArray.push(false);
        }
        break;
      default:
        break;
    }
  }
  return tallArray;
};

export const determineColStyle = (columns, idx) => {
  const style = idx && ((idx + 1) % columns === 0) ? 'pad-2-b pad-3-b-lg-up pad-2-r pad-3-r-lg-up' : 'pad-2-b pad-3-b-lg-up pad-2-r pad-3-r-lg-up';
  return style;
};

export const orderBenefits = (order, benefits) => {
  // if order does not exist, it should just return benefits unordered
  if (!order) return benefits;
  return benefits.sort((a, b) => order.indexOf(a) - order.indexOf(b));
};

export const longTileLayout = (login, tileId, longTilesLoggedInArray, longTilesLoggedOutInArray) => {
  const tileArray = login ? longTilesLoggedInArray : longTilesLoggedOutInArray;
  return tileArray.includes(tileId);
};

export const filterFromLocation = (location) => location && location.query && location.query.filter;

export const hasError = (...arguments_) => arguments_.some((argument) => argument.error);

export const getTileLayout = (loggedInLayout, loggedOutLayout, login) => (login ? loggedInLayout : loggedOutLayout);

export const getSubdomain = (origin = 'https://global.americanexpress.com') => Utils.getSubdomain(origin);

export const isCenturionCard = (loggedin, cardName) => cardName?.includes('centurion') && loggedin;

export const createCenturionButtonProps = (login, cardName, environment, accountKey) => {
  const shouldShowCenturionButton = isCenturionCard(login, cardName);
  if (shouldShowCenturionButton) {
    return {
      href: `${generateUrls(environment).CENTURION_HUB_URL}?account_key=${accountKey}`,
    };
  }
  return null;
};

export const isMobileLoggedInUser = (location) => location
  && location.query
  && location.query.ismobile;

const oneDataCards = [
  'amex-everyday',
  'blue-cash-everyday',
  'blue-cash-preferred',
  'amex-green',
  'blue',
  'marriott-bonvoy',
  'blue-sky',
  'cash-magnet',
  'amex-gold',
  'amex-everyday-preferred',
  'blue-business-cash',
  'blue-business-plus',
  'blue-for-business',
  'business-green-rewards',
  'open-benefits',
  'lowes-business-rewards',
  'marriott-bonvoy-business',
  'morgan-stanley-credit',
  'plum',
  'full-plum',
  'schwab-investor-credit-card',
  'platinum',
  'goldman-platinum-card',
  'morgan-stanley-platinum-card',
  'schwab-platinum-card',
  'business-platinum',
  'business-gold',
  'gold',
  'corporate-green',
  'corporate-gold',
  'corporate-platinum',
  'corporate-centurion',
  'centurion',
  'business-centurion',
  'marriott-bonvoy-brilliant',
  'amazon-business',
  'amazon-business-core',
  'hilton-aspire',
  'hilton-business',
  'hilton-honors',
  'hilton-surpass',
  'rewards-checking',
  'business-checking',
  'morgan-stanley-blue-cash-preferred',
  'rewards-checking',
  'business-checking',
  'delta',
  'delta-gold',
  'delta-platinum',
  'delta-reserve',
  'delta-blue',
  'gold-delta-skymiles',
  'platinum-delta-skymiles',
  'delta-reserve-business',
  'delta-skymiles',
  'simply-cash',
  'simply-cash-plus',
  'business-gold-rewards',
];

export const isCardUsingOneData = (cardname) => oneDataCards.includes(cardname);

export const getLongTiles = (benefits) => {
  const tallArray = [];
  if (!benefits) {
    return tallArray;
  }
  // eslint-disable-next-line array-callback-return -- needed
  Object.keys(benefits).map((item) => {
    if (benefits[item].details.layout.webFormat === 'Long') {
      tallArray.push(item);
    }
  });
  return tallArray;
};

export const getSpecialTiles = (benefits) => {
  const specialATiles = [];
  if (!benefits) {
    return specialATiles;
  }
  // eslint-disable-next-line array-callback-return -- needed
  Object.keys(benefits).map((item) => {
    if (benefits[item].isEnrollable) {
      specialATiles.push(item);
    }
  });
  return specialATiles;
};

export const composeCategories = (filters) => {
  const categories = [];
  if (!filters) {
    return categories;
  }
  // eslint-disable-next-line array-callback-return -- needed
  Object.keys(filters).map((item) => {
    categories.push(item);
  });
  return categories;
};

export const generateBackUrl = (login, cardName) => `/card-benefits/view-all/${login
  ? '' : cardName}`;

export const columnsToShow = (width) => {
  let columns = 3;

  // x-large
  if (width > 1280) {
    columns = 3;
    // large
  } else if (width > 1023 && width <= 1280) {
    columns = 3;
    // medium
  } else if ((width > 767) && (width <= 1023)) {
    columns = 2;
    // small
  } else if (width > 375 && width <= 767) {
    columns = 1;
    // x-small
  } else {
    columns = 1;
  }

  return columns;
};

export const setProductSelected = (productRegistry) => {
  const productType = productRegistry?.type ? productRegistry?.type : null;
  if (productType === 'CARD_PRODUCT') {
    const keyValue = `?account_key=${productRegistry.accountKey}`;
    return keyValue;
  }
  if (productType === 'AEXP_BUSINESS_CHECKING_ACCOUNT' || productType === 'AEXP_PERSONAL_CHECKING_ACCOUNT') {
    const keyValue = `?opaqueAccountId=${productRegistry.opaqueAccountId}`;
    return keyValue;
  }
  return '?no_key';
};

export const redirectToCenturionLogin = (url) => {
  if (global.BROWSER) {
    const CENTURION_HUB_LOGIN = url + encodeURIComponent(`${window.location.href}`);
    window?.location?.assign(CENTURION_HUB_LOGIN);
  }
};

export const redirectToMycaLogin = (url, path) => {
  if (global.BROWSER) {
    window.location.assign(url + encodeURIComponent(`${window.location.protocol}//${window.location.hostname}${path}`));
  }
};

export const formatCategoryName = (categoryName) => {
  if (!categoryName) return '';
  return categoryName.replace(/ /g, '-').toLowerCase();
};
